import { useEffect, useState } from "react";
import BillingInformation from "./billingInformation";
import dataService from "@/app/services/dataService";
import {
  CheckoutResponse,
  SavedBillingPayload,
  SavedPaymentPayload,
  SavedDeliveryPickupPayload,
} from "@/app/models/checkout/types";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import moment from "moment";
import PickupDelivery from "./pickupDelivery";
import BillingInformationSaved from "./billingInformationSaved";
import { PickupDeliverySaved } from "./pickupDeliverySaved";
import PaymentInformationSaved from "./paymentInformationSaved";
import PaymentInformation from "./paymentInformation";
import ItemsInOrder from "./itemsInOrder";
import OrderSummaryPanel from "./orderSummaryPanel";
import { AnimatePresence, motion } from "framer-motion";

import { STORAGE_KEYS } from "@/app/utils/localStorageHelper/checkoutKeys";
import {
  loadFromStorage,
  saveToStorage,
  clearFromStorage,
  saveFilesToStorage,
  loadFilesFromStorage,
} from "@/app/utils/localStorageHelper/checkoutStorage";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store/store";
import { isEagleViewProduct } from "@/app/models/constants";
import { GoogleAnalyticsSettings } from "@/app/models/common/types";
import { sendGAEvent } from "@/app/utils/googleAnalytics/ga4";

const Checkout: React.FC = () => {
  const [checkout, setCheckout] = useState<CheckoutResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isBillingInfoSaved, setBillingInfoSaved] = useState(false);
  const [isPaymentInfoSaved, setPaymentInfoSaved] = useState(false);
  const [billingAttachments, setAttachments] = useState<File[]>([]);
  const [billingData, setBillingData] = useState<SavedBillingPayload>(() => ({
    jobName: "",
    currentBillingAccount: "",
    attachments: [],
  }));
  const [pickupDeliveryData, setPickupDeliveryData] =
    useState<SavedDeliveryPickupPayload | null>(null);
  const [isPickupDeliverySaved, setPickupDeliverySaved] = useState(false);
  const [settings, setSettings] = useState<GoogleAnalyticsSettings | null>(
    null
  );

  const [paymentData, setPaymentData] = useState<SavedPaymentPayload>(() => ({
    selectedRewardPoints: "",
    interestedOnAccount: null,
    paymentTerms: "",
  }));
  const itemsCount = checkout?.items?.length;
  const cartItems = checkout?.items;
  const userId =
    useSelector((state: RootState) => state.userState.user.id)?.toString() ??
    "0";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await dataService.getCheckout({
          clientUtcOffsetInMinutes: moment().utcOffset(),
        });
        var settingsResponse = (await dataService.getGASettings()).data;
        setSettings(settingsResponse);

        sendGAEvent("begin_checkout", {
          currency: "USD",
          value: res.data.payment?.orderTotal,
          coupon: res.data.discountCode ?? "none",
          items: (res.data.items ?? []).map((it: any, i: number) => ({
            item_id: it.id ?? "",
            item_name: it.productName ?? "(unknown product)",
            affiliation: settingsResponse.storeName ?? "LansingNOW",
            index: i + 1,
            item_brand: it.manufacturers?.[0] ?? "",
            item_variant: it.productName ?? "(unknown product variant)",
            price: Number(
              it.unitPriceInclTax ?? it.priceInclTax ?? it.unitPrice ?? 0
            ).toFixed(2),
            quantity: it.quantity ?? it.qty ?? 1,
            item_category: it.category ?? it.categories?.[0] ?? "",
            item_category2: it.categories?.[1] ?? "",
            item_category3: it.categories?.[2] ?? "",
            item_category4: it.categories?.[3] ?? "",
            item_category5: it.categories?.[4] ?? "",
          })),
        });

        if (!cancelled && res?.success && res.data) {
          if (res.data.redirectToCart) {
            window.location.href = "/lbpcustomer/cart";
          } else {
            const hasEV = (res.data.items ?? []).some((item: any) =>
              isEagleViewProduct(item.sku)
            );
            const hasAddress = hasEV
              ? !!loadFromStorage<{ value: unknown }>(
                  userId,
                  STORAGE_KEYS.EAGLEVIEW_ADDRESS_PREFILL
                )
              : true;
            if (hasEV && !hasAddress) {
              window.location.href = "/lbpcustomer/cart";
            } else {
              setCheckout(res.data);
            }
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const billingCached = loadFromStorage<SavedBillingPayload>(
      userId,
      STORAGE_KEYS.BILLING
    );
    const pickupDeliveryCached = loadFromStorage<SavedDeliveryPickupPayload>(
      userId,
      STORAGE_KEYS.PICKUP_DELIVERY
    );
    const paymentCached = loadFromStorage<SavedPaymentPayload>(
      userId,
      STORAGE_KEYS.PAYMENT
    );
    const attachments = loadFilesFromStorage(
      userId,
      STORAGE_KEYS.BILLING_ATTACHMENTS
    );
    if (billingCached) {
      if (attachments) setAttachments(attachments);
      setBillingData(billingCached);
      setBillingInfoSaved(true);
    }
    if (pickupDeliveryCached) {
      setPickupDeliveryData(pickupDeliveryCached);
      setPickupDeliverySaved(true);
    }
    if (paymentCached) {
      setPaymentData(paymentCached);
      setPaymentInfoSaved(true);
    }
  }, []);

  useEffect(() => {
    if (!isBillingInfoSaved) return;

    const cached = loadFromStorage<SavedBillingPayload>(
      userId,
      STORAGE_KEYS.BILLING
    );
    if (cached) {
      setBillingData(cached);
      setBillingInfoSaved(true);
    }
  }, [isBillingInfoSaved]);

  useEffect(() => {
    if (!isPickupDeliverySaved) return;

    const cached = loadFromStorage<SavedDeliveryPickupPayload>(
      userId,
      STORAGE_KEYS.PICKUP_DELIVERY
    );
    if (cached) {
      setPickupDeliveryData(cached);
      setPickupDeliverySaved(true);
    }
  }, [isPickupDeliverySaved]);

  useEffect(() => {
    if (!isPaymentInfoSaved) return;

    const cached = loadFromStorage<SavedPaymentPayload>(
      userId,
      STORAGE_KEYS.PAYMENT
    );
    if (cached) {
      setPaymentData(cached);
      setPaymentInfoSaved(true);
    }
  }, [isPaymentInfoSaved]);

  useEffect(() => {
    if (!checkout) return;
    const hasEV = checkout.items?.some((item) => isEagleViewProduct(item.sku)) ?? false;
    if (hasEV && isPickupDeliverySaved && pickupDeliveryData && !pickupDeliveryData.isEagleViewDelivery) {
      clearFromStorage(userId, STORAGE_KEYS.PICKUP_DELIVERY);
      setPickupDeliverySaved(false);
      setPickupDeliveryData(null);
    }
  }, [checkout]);

  if (loading) {
    return (
      <Box p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!checkout) {
    return <Box p={3}>Unable to load checkout information.</Box>;
  }
  const cartHasEagleView = checkout.items?.some((item) => isEagleViewProduct(item.sku)) ?? false;
  const isEagleViewLockedForCheckout = cartHasEagleView;

  const handleBackClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.location.href = `/lbpcustomer/cart`;
  };
  return (
    <>
      <Box sx={{ px: 2, pt: 1 }}>
        <a
          onClick={handleBackClick}
          aria-label={"BACK TO CART"}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            textDecoration: "none",
            fontWeight: 400,
            fontSize: 12,
            color: "black",
            cursor: "pointer",
          }}
        >
          <span>←</span>
          {"BACK TO CART"}
        </a>
      </Box>

      <Grid container padding={2}>
        <Grid item xs={12} sm={6} md={7} lg={7}>
          <Box sx={{ p: { xs: 0, sm: 4 }, mb: { xs: 6, sm: 0 } }}>
            <AnimatePresence mode="wait">
              {isBillingInfoSaved ? (
                <motion.div
                  key="saved"
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  style={{ overflow: "hidden" }}
                >
                  <BillingInformationSaved
                    accountNumber={checkout.customerNum}
                    customerName={checkout.customerName}
                    savedBillingPayload={billingData}
                    attachments={billingAttachments}
                    onEdit={() => {
                      setBillingInfoSaved(false);
                      setPickupDeliverySaved(false);
                      setPaymentInfoSaved(false);
                      clearFromStorage(userId, STORAGE_KEYS.PICKUP_DELIVERY);
                      clearFromStorage(userId, STORAGE_KEYS.PAYMENT);

                      sendGAEvent("billing_edit", {
                        event: "billing_edit",
                        account_num: checkout.customerNum,
                        job_name: billingData.jobName,
                        customer_ship_to_name: checkout.customerShipToName,
                        customer_ship_to_address:
                          checkout.customerShipToAddress,
                      });
                    }}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  style={{ overflow: "hidden" }}
                >
                  <BillingInformation
                    customerNum={checkout.customerNum}
                    customerName={checkout.customerName}
                    customerShipToId={checkout.customerShipToId}
                    customerShipToName={checkout.customerShipToName}
                    customerShipToAddress={checkout.customerShipToAddress}
                    onSave={(data) => {
                      setBillingData(data);
                      setAttachments(data.attachments);
                      setBillingInfoSaved(true);
                      saveToStorage(userId, STORAGE_KEYS.BILLING, data);
                      saveFilesToStorage(
                        userId,
                        STORAGE_KEYS.BILLING_ATTACHMENTS,
                        data.attachments
                      );

                      sendGAEvent("billing_done", {
                        event: "billing_done",
                        account_num: checkout.customerNum,
                        job_name: data.jobName,
                        customer_ship_to_name: checkout.customerShipToName,
                        customer_ship_to_address:
                          checkout.customerShipToAddress,
                      });
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
          <Box sx={{ p: { xs: 0, sm: 4 }, mb: { xs: 6, sm: 0 } }}>
            <AnimatePresence mode="wait">
              {isPickupDeliverySaved ? (
                <motion.div
                  key="saved"
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  style={{ overflow: "hidden" }}
                >
                  <PickupDeliverySaved
                    isEagleViewLocked={cartHasEagleView}
                    onEdit={() => {
                      setPickupDeliverySaved(false);
                      setPaymentInfoSaved(false);
                      clearFromStorage(userId, STORAGE_KEYS.PAYMENT);

                      sendGAEvent("shipping_edit", {
                        event: "shipping_edit",
                        shipping_type: pickupDeliveryData?.isPickup
                          ? "pickup"
                          : "delivery",
                        deliveryAddress: pickupDeliveryData?.deliveryAddress,
                        pickupLocation: pickupDeliveryData?.pickupLocation,
                        date_needed: pickupDeliveryData?.selectedDate ?? "",
                        time_needed: pickupDeliveryData?.selectedTime ?? "",
                        order_notes: pickupDeliveryData?.note ?? "",
                      });
                    }}
                    hasSpecialOrderItems={
                      checkout.shippingAddress.hasSpecialOrderItems
                    }
                    payload={pickupDeliveryData}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  style={{ overflow: "hidden" }}
                >
                  <PickupDelivery
                    model={checkout}
                    open={isBillingInfoSaved}
                    isEagleViewLocked={isEagleViewLockedForCheckout}
                    onSave={(data) => {
                      setPickupDeliveryData(data);
                      setPickupDeliverySaved(true);
                      saveToStorage(userId, STORAGE_KEYS.PICKUP_DELIVERY, data);

                      sendGAEvent("shipping_done", {
                        event: "shipping_done",
                        shipping_type: data.isPickup ? "pickup" : "delivery",
                        deliveryAddress: data.deliveryAddress,
                        pickupLocation: data.pickupLocation,
                        date_needed: data.selectedDate ?? "",
                        time_needed: data.selectedTime ?? "",
                        order_notes: data.note ?? "",
                      });
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
          <Box sx={{ p: { xs: 0, sm: 4 }, mb: { xs: 6, sm: 0 } }}>
            <AnimatePresence mode="wait">
              {isPaymentInfoSaved ? (
                <motion.div
                  key="saved"
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  style={{ overflow: "hidden" }}
                >
                  <PaymentInformationSaved
                    savedPaymentPayload={paymentData}
                    enablePromotionsSection={checkout.enablePromotionsSection}
                    onEdit={() => {
                      setPaymentInfoSaved(false);
                      clearFromStorage(userId, STORAGE_KEYS.PAYMENT);

                      sendGAEvent("payment_edit", {
                        event: "payment_edit",
                        payment_type:
                          paymentData.paymentTerms == "On Account"
                            ? "On Account"
                            : "Cash",
                        interested_on_account: paymentData.interestedOnAccount
                          ? "1"
                          : "0",
                        reward_points: paymentData.selectedRewardPoints ?? "",
                      });
                    }}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  style={{ overflow: "hidden" }}
                >
                  <PaymentInformation
                    open={isBillingInfoSaved && isPickupDeliverySaved}
                    payment={checkout.payment}
                    showOnAccountRequest={checkout.showOnAccountRequest}
                    useImpactRewards={checkout.useImpactRewards}
                    enablePromotionsSection={checkout.enablePromotionsSection}
                    initialPayment={paymentData}
                    onSave={(data) => {
                      setPaymentData(data);
                      setPaymentInfoSaved(true);
                      saveToStorage(userId, STORAGE_KEYS.PAYMENT, data);

                      sendGAEvent("payment_done", {
                        event: "payment_done",
                        payment_type:
                          data.paymentTerms == "On Account"
                            ? "On Account"
                            : "Cash",
                        interested_on_account: data.interestedOnAccount
                          ? "1"
                          : "0",
                        reward_points: data.selectedRewardPoints ?? "",
                      });
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <Box sx={{ mt: 5 }}>
              <Box sx={{ p: 4, bgcolor: "#f6f6f6", borderRadius: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: "24px",
                  }}
                >
                  <Typography
                    component="div"
                    sx={{
                      fontFamily: "Montserrat, Verdana, sans-serif",
                      fontWeight: 600,
                      fontSize: { xs: 26, md: 36 },
                      lineHeight: { xs: "32px", md: "44px" },
                      color: "#767676",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Items in Order{" "}
                    <Typography
                      component="span"
                      sx={{ fontSize: "36px", fontWeight: 600 }}
                    >
                      ({itemsCount})
                    </Typography>
                  </Typography>
                </Box>

                {cartItems?.map((item, index) => (
                  <ItemsInOrder key={index} item={item} />
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={5} lg={5}>
          <OrderSummaryPanel
            enablePromotions={checkout.enablePromotionsSection}
            enablePlaceOrder={
              isPaymentInfoSaved && isBillingInfoSaved && isPickupDeliverySaved
            }
            contactEmail={checkout.pickupPoint.contactEmail}
            contactPhoneNumber={checkout.pickupPoint.contactPhoneNumber}
            itemsCount={itemsCount ?? 0}
            discountCode={checkout.discountCode}
            reachedPromoCodeMaxDiscount={checkout.reachedPromoCodeMaxDiscount}
            redeemedRewardPoints={Number(paymentData.selectedRewardPoints)}
            orderTotal={checkout.payment.orderTotal}
            maxApplicableRewardPoints={
              checkout.payment.maxApplicableRewardPoints
            }
            discount={checkout.discount}
            quoteEnabled={checkout.quoteCheckBoxEnabled}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default Checkout;
